import { documentQuery } from "@common/api/documents"
import { LoadingState } from "@common/components/shared/loading-state"
import { Button } from "@common/components/ui/button"
import { useMaxWidth } from "@common/hooks/use-mobile"
import { cn } from "@common/lib/utils"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { FileText, Info } from "lucide-react"
import { Suspense, useMemo } from "react"
import { z } from "zod"
import { DocumentViewer } from "@/components/document/document-viewer"
import { DETAIL_SHEET_BREAKPOINT } from "@/components/layout/detail-panel"
import { requirePermission } from "@/lib/require-permission"
import { decodeSourceEvidence, sourceSearchSchema } from "@/lib/source-evidence"
import { DownloadDocumentButton } from "@/routes/_layout/documents/-components/download-document-button"
import { documentNumber } from "@/routes/_layout/documents/-lib/metadata"
import { DirectivePanel } from "./-components/directive-panel"

const FILE_BAR = cn("flex h-10 shrink-0 items-center gap-2", "border-b px-4")

const searchSchema = sourceSearchSchema.extend({
  details: z.boolean().optional().catch(undefined),
})

export const Route = createFileRoute(
  "/_layout/airworthiness-directives_/$documentId",
)({
  validateSearch: (search) => searchSchema.parse(search),
  beforeLoad: async ({ context: { queryClient } }) => {
    await requirePermission(queryClient, "document:read", "/")
  },
  loader: async ({ context: { queryClient }, params: { documentId } }) => {
    const detail = await queryClient.ensureQueryData(
      documentQuery({ document_id: documentId }),
    )
    return {
      breadcrumb: [
        {
          label: "Airworthiness Directives",
          href: "/airworthiness-directives",
        },
        { label: documentNumber(detail) ?? detail.file_name },
      ],
    }
  },
  component: DirectivePage,
})

function DirectivePage(): React.ReactElement {
  const { documentId } = Route.useParams()
  const { page, source, details } = Route.useSearch()
  const compact = useMaxWidth(DETAIL_SHEET_BREAKPOINT)
  const open = details ?? !compact
  const navigate = useNavigate({ from: Route.fullPath })
  const sourceEvidence = useMemo(() => decodeSourceEvidence(source), [source])
  const { data: detail } = useSuspenseQuery(
    documentQuery({ document_id: documentId }),
  )
  const showDetails = (next: boolean) =>
    navigate({
      search: (previous) => ({ ...previous, details: next }),
      replace: true,
    })

  return (
    <div className="flex h-full flex-col">
      <FileBar
        fileName={detail.file_name}
        details={open}
        onToggle={() => showDetails(!open)}
      />
      <div className="flex min-h-0 flex-1">
        <div className="min-w-0 flex-1">
          <Suspense fallback={<LoadingState />}>
            <DocumentViewer
              documentId={documentId}
              page={page}
              sourceEvidence={sourceEvidence}
              action={
                <DownloadDocumentButton
                  documentId={documentId}
                  fileName={detail.file_name}
                />
              }
            />
          </Suspense>
        </div>
        {open && (
          <DirectivePanel
            directive={detail}
            onClose={() => showDetails(false)}
          />
        )}
      </div>
    </div>
  )
}

function FileBar({
  fileName,
  details,
  onToggle,
}: {
  fileName: string
  details: boolean
  onToggle: () => void
}): React.ReactElement {
  return (
    <div className={FILE_BAR}>
      <FileText className="size-3.5 shrink-0 text-muted-foreground" />
      <span className="truncate text-small-medium">{fileName}</span>
      <Button
        variant="ghost"
        size="icon-sm"
        className={cn("ml-auto", details && "bg-muted")}
        aria-label="Details"
        aria-pressed={details}
        onClick={onToggle}
      >
        <Info />
      </Button>
    </div>
  )
}
